import { Parser, type Expression as ParsedExpression } from 'expr-eval';
import parserFunctions from 'src/utils/parserFunctions';

import type Node from './nodes/Node';

const parser = new Parser({
    operators: {
        ceil: false,
        floor: false,
        max: false,
        min: false,
        round: false,
    },
});

Object.assign(parser.functions, parserFunctions);

/**
 * The `in` operator in expr-eval performs a linear O(n) array scan (see its `contains`
 * helper). For large right-hand arrays we back membership with a Set. The Set is cached per
 * array instance (WeakMap) so repeated probes against the same array skip the rebuild.
 *
 * Set membership uses SameValueZero rather than `===`; the only observable difference is
 * NaN (SameValueZero considers NaN equal to itself), which is not a meaningful `in` operand.
 * Non-array RHS and small arrays fall through to the original operator unchanged.
 */
const IN_SET_THRESHOLD = 12;
const inSetCache = new WeakMap<any[], Set<unknown>>();
const parserBinaryOps = (parser as any).binaryOps as Record<string, (a: any, b: any) => any>;
const originalInOperator = parserBinaryOps.in;
parserBinaryOps.in = (a: any, b: any) => {
    if (Array.isArray(b) && b.length >= IN_SET_THRESHOLD) {
        let set = inSetCache.get(b);
        if (!set) {
            set = new Set(b);
            inSetCache.set(b, set);
        }
        return set.has(a);
    }
    return originalInOperator(a, b);
};

export const COMPILED_EXPR_CACHE_LIMIT = 1000;

type CompiledMath = {
    text: string;
    vars: Record<string, string>;
    compiled: ParsedExpression;
};

/**
 * Per-Transformation compilation cache: parsed math expressions and built Node instances,
 * reused across executions of a single Transformation. Owned by the Transformation instance so
 * it is reclaimed when a consumer's compiled-transformation TTL cache purges the Transformation,
 * rather than persisting for the whole process. Internal to the engine — not exported from the
 * package. Held privately by Transformation and injected into each per-execution Context.
 */
export class TransformationCache {
    /**
     * Cache of compiled math/eval expressions, keyed by the RAW math string. Holds the substituted
     * `text`, the `vars` placeholder→path map, and the parsed `compiled` expression — all
     * deterministic from `math`, so the substitution regex and the tokenize/parse each run once per
     * distinct expression instead of on every row/item; only the resolved variable *values* vary
     * per call. The substitution fn is injected (rather than imported) to keep this module free of a
     * Context import. The working set is naturally bounded per Transformation (distinct math texts
     * are fixed by the static expression tree, ~100 for the largest transforms); the size cap is a
     * defensive guard against pathological input-derived texts (oldest evicted first).
     */
    private readonly compiledMathCache = new Map<string, CompiledMath>();

    /**
     * Cache of Node instances, keyed on the raw expression object. A node is constructed once
     * (migrate + type-transform + default-merge + validation) and reused on every subsequent
     * evaluation of that expression — including per-item re-evaluation inside container nodes —
     * instead of being rebuilt each time (see Context.execute). WeakMap so any runtime-generated
     * expression objects are collected rather than accumulating. Nodes hold no per-execution state
     * (the Context is passed to execute()), so reuse is concurrency-safe.
     */
    private readonly nodeCache = new WeakMap<object, Node<any>>();

    getCompiledMath(
        math: string,
        substitute: (m: string) => { text: string; vars: Record<string, string> },
    ): CompiledMath {
        let entry = this.compiledMathCache.get(math);
        if (entry === undefined) {
            const { text, vars } = substitute(math);
            const compiled = parser.parse(text);
            if (this.compiledMathCache.size >= COMPILED_EXPR_CACHE_LIMIT) {
                const oldest = this.compiledMathCache.keys().next().value;
                if (oldest !== undefined) this.compiledMathCache.delete(oldest);
            }
            entry = { text, vars, compiled };
            this.compiledMathCache.set(math, entry);
        }
        return entry;
    }

    getNode(expression: object, NodeType: new (expression: any) => Node<any>): Node<any> {
        let node = this.nodeCache.get(expression);
        if (!node) {
            node = new NodeType(expression);
            this.nodeCache.set(expression, node);
        }
        return node;
    }
}
